import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import logo from "./assets/logofinal-flat.png";
import useCaseRealEstate from "./assets/homev2-usecase-realestate-clean.png";
import useCaseLouvre from "./assets/homev2-usecase-louvre.png";
import useCaseBooking from "./assets/homev2-usecase-booking.png";
import useCaseRome from "./assets/homev2-usecase-rome.png";
import useCaseConcert from "./assets/homev2-usecase-concert.png";
import useCaseIndustry from "./assets/homev2-usecase-industry.png";
import useCaseCnn from "./assets/homev2-usecase-cnn.png";
import weaverNetworkImage from "./assets/homev2-weaver-network.png";
import architectureImage from "./assets/homev2-living-weave-architecture.png";
import heroImage from "./assets/indexAll.png";
import relayVideo from "./assets/relay_adidas_final.mp4";
import weaveVideo from "./assets/vitrine/loom/weave.mp4";
import mapSectionVideo from "./assets/map/map_section1.mp4";
import "./HomeV2.css";
import "./HomeV2.login-overlap-fix.css";
import { PublicFooter, PublicHeaderNav } from "./PublicSiteChrome";

const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.akremanaya.weavevomobile&hl=fr";
const REGISTER_FORM_URL = "/join-beta";
const APPLE_STORE_URL = "/join-beta";
const LEGACY_AUTH_URL = "https://weavevo.com/auth/realms/master/protocol/openid-connect/auth";
const LEGACY_AUTH_REDIRECT_URI = "https://weavevo.com/auth/callback";
const LEGACY_AUTH_CLIENT_ID = "weavevo-frontend";
const LEGACY_AUTH_TOKEN_URL = "https://weavevo.com/auth/realms/master/protocol/openid-connect/token";
const LEGACY_AUTH_USERINFO_URL = "https://weavevo.com/auth/realms/master/protocol/openid-connect/userinfo";
const KEYCLOAK_CREATE_ACCOUNT_URL = "https://weavevo.com/auth/realms/master/protocol/openid-connect/auth?client_id=weavevo-frontend&redirect_uri=https%3A%2F%2Fweavevo.com%2Fauth%2Fcallback&response_type=code&scope=openid&prompt=create&kc_idp_hint=";

const trackHomeV2 = (eventName, params = {}) => {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", eventName, {
      event_category: "home_v2",
      ...params,
    });
  } catch {
    // ignore analytics issues on public page
  }
};


const LinkButton = ({ href, children, className = "", onClickName, external = false }) => (
  <a
    className={className}
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noreferrer" : undefined}
    onClick={() => onClickName && trackHomeV2(onClickName, { href })}
  >
    {children}
  </a>
);

const MiniPoint = ({ title, children }) => (
  <article className="hv2-miniPoint">
    <h4>{title}</h4>
    {children ? <p>{children}</p> : null}
  </article>
);

const UseCaseSlide = ({
  id,
  image,
  imageAlt,
  category,
  title,
  children,
  ctaLabel,
  ctaHref = REGISTER_FORM_URL,
  imageHref,
}) => (
  <article className="hv2-useSlide" id={id}>
    <div className="hv2-useImagePanel">
      {imageHref ? (
        <a className="hv2-useImageLink" href={imageHref}>
          <img src={image} alt={imageAlt} loading="lazy" />
        </a>
      ) : (
        <img src={image} alt={imageAlt} loading="lazy" />
      )}
    </div>

    <div className="hv2-useTextPanel">
      <div className="hv2-useEyebrow">{category}</div>
      <h3>{title}<sup className="hv2-useCaseAsterisk">*</sup></h3>
      <div className="hv2-useNarrative">{children}</div>


      <LinkButton href={ctaHref} className="hv2-inlineCta" onClickName="use_case_register_click">
        {ctaLabel}
      </LinkButton>
    </div>
  </article>
);

export default function HomeV2() {
  const navigate = useNavigate();
  const { login, token } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");


  const playHoverVideoWithSound = (event) => {
    const video = event.currentTarget?.querySelector?.("video");
    if (!video) return;
    video.muted = false;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  const keepHoverVideoPlayingMuted = (event) => {
    const video = event.currentTarget?.querySelector?.("video");
    if (!video) return;
    video.muted = true;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  };

  // Preserve the previous working Weavevo password authentication mechanism:
  // HomeV2 -> Keycloak token endpoint -> backend -> Keycloak token endpoint.
  // Do NOT change this endpoint to direct authentication and do NOT redirect the
  // password flow to the Keycloak login page.
  const handlePasswordLogin = async (event) => {
    event.preventDefault();

    const cleanUsername = String(username || "").trim();

    if (!cleanUsername || !password) {
      setLoginError("Please enter your username/email and password.");
      return;
    }

    setLoginLoading(true);
    setLoginError("");

    try {
      // Do not call the Keycloak token endpoint directly from the browser.
      // The previous working password flow goes through the backend wrapper.
      const response = await fetch("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: cleanUsername,
          password,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data?.token) {
        throw new Error(data?.error || data?.message || "Login failed.");
      }

      login(data.token, data.user || {});
      trackHomeV2("password_login_success", {
        auth_provider: "backend_password_login",
        username: cleanUsername,
      });

      navigate("/public");
    } catch (error) {
      console.error("HomeV2 password login error:", error);
      setLoginError(error?.message || "Unable to sign in.");
      trackHomeV2("password_login_error", {
        auth_provider: "backend_password_login",
        message: error?.message || "Unable to sign in.",
      });
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const params = new URLSearchParams({
      client_id: LEGACY_AUTH_CLIENT_ID,
      redirect_uri: LEGACY_AUTH_REDIRECT_URI,
      response_type: "code",
      scope: "openid profile email",
      kc_idp_hint: "google",
    });

    trackHomeV2("google_login_click", {
      auth_provider: "google_via_keycloak",
      redirect_uri: LEGACY_AUTH_REDIRECT_URI,
    });

    localStorage.setItem("weavevo_post_login_redirect", "/public");
    window.location.href = `${LEGACY_AUTH_URL}?${params.toString()}`;
  };

  useEffect(() => {
    document.title = "Weavevo | The AI-Powered Modular Living Video Platform";
    trackHomeV2("loaded", {
      page_path: window.location.pathname,
      page_location: window.location.href,
    });
  }, []);

  // Keep the floating login card fixed while there is room, then let it stop
  // immediately above the footer instead of painting over footer links.
  useEffect(() => {
    const footer = document.getElementById("site-footer");
    const loginPanel = document.querySelector(".hv2-directLoginPanel");
    if (!footer || !loginPanel) return undefined;

    let rafId = 0;

    const updateLoginFooterStop = () => {
      if (rafId) return;

      rafId = window.requestAnimationFrame(() => {
        rafId = 0;

        // The desktop login panel is fixed. When the footer rises toward it,
        // move the panel upward by exactly the collision amount. This makes
        // the panel behave like "fixed until footer" and then scroll away.
        const footerTop = footer.getBoundingClientRect().top;
        const panelHeight = loginPanel.getBoundingClientRect().height;
        const computedTop = Number.parseFloat(window.getComputedStyle(loginPanel).top);
        const panelTop = Number.isFinite(computedTop) ? computedTop : 72;
        const safeGap = 24;
        const footerShift = Math.min(0, footerTop - safeGap - panelHeight - panelTop);

        document.documentElement.style.setProperty(
          "--hv2-login-footer-shift",
          `${footerShift}px`
        );
      });
    };

    updateLoginFooterStop();
    window.addEventListener("scroll", updateLoginFooterStop, { passive: true });
    window.addEventListener("resize", updateLoginFooterStop);

    return () => {
      window.removeEventListener("scroll", updateLoginFooterStop);
      window.removeEventListener("resize", updateLoginFooterStop);
      if (rafId) window.cancelAnimationFrame(rafId);
      document.documentElement.style.removeProperty("--hv2-login-footer-shift");
    };
  }, [token]);

  return (
    <div className="hv2-page">
      <header className="hv2-nav hv2-navPersistent" aria-label="Weavevo navigation">
        <a className="hv2-brand" href="/" aria-label="Weavevo home">
          <img src={logo} alt="Weavevo" />
          <span>weavevo</span>
        </a>

        <PublicHeaderNav loginHref="#product" />
      </header>

      <main>
        <section className="hv2-heroImageSection hv2-heroRevealSection" id="product" aria-label="Weavevo public home hero">
          <img
            className="hv2-heroImage hv2-heroRevealImage"
            src={heroImage}
            alt="Weavevo reveal hero showing the public landing message and mobile product preview"
          />

          <a
            className="hv2-heroHotspot hv2-heroHotspotBeta"
            href={REGISTER_FORM_URL}
            aria-label="Request Weavevo beta access"
            onClick={() => trackHomeV2("hero_image_join_beta_click", { href: REGISTER_FORM_URL })}
          />

          <a
            className="hv2-heroHotspot hv2-heroHotspotGooglePlay"
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noreferrer"
            aria-label="Open Google Play"
            onClick={() => trackHomeV2("hero_image_google_play_click", { href: GOOGLE_PLAY_URL })}
          />

          {!token ? (
          <aside className="hv2-directLoginPanel" aria-label="Weavevo login access">
            <form onSubmit={handlePasswordLogin}>
              <input
                type="text"
                name="username"
                autoComplete="username"
                placeholder="Username or email"
                aria-label="Username or email"
                value={username}
                disabled={loginLoading}
                onChange={(event) => setUsername(event.target.value)}
                style={{
                  width: "100%",
                  minHeight: "44px",
                  marginBottom: "10px",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "1px solid rgba(255,255,255,.18)",
                  background: "rgba(255,255,255,.08)",
                  color: "#fff",
                  outline: "none",
                }}
              />

              <input
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder="Password"
                aria-label="Password"
                value={password}
                disabled={loginLoading}
                onChange={(event) => setPassword(event.target.value)}
                style={{
                  width: "100%",
                  minHeight: "44px",
                  marginBottom: "10px",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  border: "1px solid rgba(255,255,255,.18)",
                  background: "rgba(255,255,255,.08)",
                  color: "#fff",
                  outline: "none",
                }}
              />

              {loginError ? (
                <div
                  role="alert"
                  style={{
                    marginBottom: "10px",
                    color: "#ffd6df",
                    fontSize: "12px",
                  }}
                >
                  {loginError}
                </div>
              ) : null}

              <button type="submit" disabled={loginLoading}>
                {loginLoading ? "Signing in..." : "Sign in"}
                <span aria-hidden="true">→</span>
              </button>
            </form>

            <div
              aria-hidden="true"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                margin: "14px 0",
                color: "rgba(255,255,255,.55)",
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: ".08em",
              }}
            >
              <span style={{ flex: 1, height: "1px", background: "rgba(255,255,255,.14)" }} />
              or
              <span style={{ flex: 1, height: "1px", background: "rgba(255,255,255,.14)" }} />
            </div>

            <button
              type="button"
              className="hv2-googleLoginButton"
              onClick={handleGoogleLogin}
              disabled={loginLoading}
              aria-label="Log in with Google"
            >
              <svg className="hv2-googleIcon" viewBox="0 0 18 18" aria-hidden="true">
                <path fill="#4285F4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.797 2.715v2.258h2.909c1.702-1.567 2.684-3.879 2.684-6.614Z" />
                <path fill="#34A853" d="M9 18c2.43 0 4.468-.806 5.956-2.181l-2.909-2.258c-.806.54-1.835.859-3.047.859-2.344 0-4.328-1.585-5.037-3.714H.956v2.332A9 9 0 0 0 9 18Z" />
                <path fill="#FBBC05" d="M3.963 10.706A5.414 5.414 0 0 1 3.682 9c0-.592.102-1.167.281-1.706V4.962H.956A9 9 0 0 0 0 9c0 1.452.347 2.827.956 4.038l3.007-2.332Z" />
                <path fill="#EA4335" d="M9 3.58c1.321 0 2.507.454 3.441 1.346l2.581-2.581C13.464.892 11.427 0 9 0A9 9 0 0 0 .956 4.962l3.007 2.332C4.672 5.165 6.656 3.58 9 3.58Z" />
              </svg>
              <span>Log in with Google</span>
            </button>

            <div className="hv2-createAccountAccess">
              <span>No account yet?</span>
              <a
                href={KEYCLOAK_CREATE_ACCOUNT_URL}
                onClick={() => {
                  localStorage.setItem("weavevo_post_login_redirect", "/public");
                  trackHomeV2("create_account_click", {
                    auth_provider: "keycloak_registration",
                  });
                }}
              >
                Create an account
              </a>
            </div>
          </aside>
          ) : null}
        </section>

        <section className="hv2-section hv2-highlights hv2-globalHomeSection" id="highlights">
          <div className="hv2-sectionHead hv2-sectionHeadCompact hv2-highlightsTitle">
            <h2>Living Content That Never Goes Obsolete</h2>
          </div>

          <p className="hv2-globalHomeLead">
            Video should never be a frozen, static file that becomes obsolete the moment it is exported.
            <br />
            On Weavevo, a Weave is a modular, dynamic narrative organism that evolves over time.
          </p>

          <div
            className="hv2-globalHomeVideoWrap"
            onMouseEnter={playHoverVideoWithSound}
            onMouseLeave={keepHoverVideoPlayingMuted}
          >
            <video
              className="hv2-globalHomeVideo"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              tabIndex={0}
              aria-label="Weavevo evolving Weave video preview"
              onFocus={(event) => { event.currentTarget.muted = false; event.currentTarget.play().catch(() => {}); }}
              onBlur={(event) => { event.currentTarget.muted = true; event.currentTarget.play().catch(() => {}); }}
            >
              <source src={weaveVideo} type="video/mp4" />
            </video>
          </div>
        </section>






        <section className="hv2-section hv2-useCases" id="use-cases">
          <div className="hv2-sectionHead hv2-sectionHeadCompact">
            <h2>Stop Creating Static Media. Build Evolving Weaves.</h2>
          </div>

          <div className="hv2-useRail" aria-label="Weavevo use cases">
            <UseCaseSlide
              id="use-case-booking"
              image={useCaseBooking}
              imageAlt="Hospitality use case showing hotel team content and a mobile guest story"
              category="Hospitality and travel"
              title="Turn Hotel Listings into Evolving Guest Experiences"
              ctaLabel="Explore OTA Use Cases →"
              ctaHref="/use-cases/booking"
              imageHref="/use-cases/booking"
            >
              <MiniPoint title="Staff-powered updates">
                Teams capture authentic moments from service, rooms, food and local recommendations.
              </MiniPoint>
              <MiniPoint title="More confidence before booking">
                Guests see a current, human view of the stay instead of relying only on static galleries.
              </MiniPoint>
            </UseCaseSlide>

            <UseCaseSlide
              id="use-case-real-estate"
              image={useCaseRealEstate}
              imageAlt="Real estate use case showing property media and local context"
              category="Organic real estate marketing"
              title="Property Listings That Evolve with the Neighborhood"
              ctaLabel="Explore Real Estate Use Cases →"
              ctaHref="/use-cases/real-estate"
              imageHref="/use-cases/real-estate"
            >
              <MiniPoint title="Context beyond the property">
                Connect the home with nearby places, services and new neighborhood signals.
              </MiniPoint>
              <MiniPoint title="Always-current storytelling">
                New clips can enrich the same Weave without rebuilding the listing from scratch.
              </MiniPoint>
            </UseCaseSlide>

            <UseCaseSlide
              id="use-case-louvre"
              image={useCaseLouvre}
              imageAlt="Museum and heritage use case showing expert voices connected into a mobile story"
              category="Culture, tourism and education"
              title="Museum Stories That Grow with New Context"
              ctaLabel="Explore Cultural Heritage Cases →"
              ctaHref="/use-cases/museum"
              imageHref="/use-cases/museum"
            >
              <MiniPoint title="Connect expert perspectives">
                Curators, researchers and archives add context around the same object or exhibition.
              </MiniPoint>
              <MiniPoint title="A richer visitor experience">
                Visitors explore concise, multi-perspective stories directly on mobile.
              </MiniPoint>
            </UseCaseSlide>

            <UseCaseSlide
              id="use-case-rome"
              image={useCaseRome}
              imageAlt="Rome use case showing teachers, guides and students contributing to a city story"
              category="Tourism, culture and creator communities"
              title="A City Told Through Many Local Voices"
              ctaLabel="Explore Tourism Use Cases →"
              ctaHref="/use-cases/hotel"
              imageHref="/use-cases/hotel"
            >
              <MiniPoint title="Distributed creation">
                Guides, students, historians and local creators contribute short, authentic perspectives.
              </MiniPoint>
              <MiniPoint title="Reusable city capsules">
                The same content can feed tourism apps, campaigns and destination storytelling.
              </MiniPoint>
            </UseCaseSlide>

            <UseCaseSlide
              id="use-case-concert"
              image={useCaseConcert}
              imageAlt="Concert use case showing fan clips woven into one collective event story"
              category="Live events and fan communities"
              title="Turn Fan Clips into One Collective Event Story"
              ctaLabel="Explore Live Event Cases →"
              ctaHref="/use-cases/live-events"
              imageHref="/use-cases/live-events"
            >
              <MiniPoint title="Capture every angle">
                Fan videos and official footage become structured around shared moments.
              </MiniPoint>
              <MiniPoint title="Keep the event alive">
                Backstage clips and later contributions can keep enriching the Weave after the show.
              </MiniPoint>
            </UseCaseSlide>

            <UseCaseSlide
              id="use-case-industry"
              image={useCaseIndustry}
              imageAlt="Industrial use case showing workers capturing reusable operational knowledge"
              category="Industry 4.0 and manufacturing"
              title="Preserve Shop-Floor Knowledge as Reusable Video Skills"
              ctaLabel="Explore Industry Use Cases →"
              ctaHref="/use-cases/industry"
              imageHref="/use-cases/industry"
            >
              <MiniPoint title="Capture knowledge where it happens">
                Operators record setups, checks and repair know-how directly on the shop floor.
              </MiniPoint>
              <MiniPoint title="Build searchable machine memory">
                Reusable video knowledge supports onboarding, handovers and faster troubleshooting.
              </MiniPoint>
            </UseCaseSlide>

            <UseCaseSlide
              id="use-case-cnn"
              image={useCaseCnn}
              imageAlt="News use case showing distributed footage organized into an evolving news story"
              category="News, media and real-time reporting"
              title="Organize Distributed Footage into an Evolving News Weave"
              ctaLabel="Explore Media & News Cases →"
              ctaHref="/use-cases/media"
              imageHref="/use-cases/media"
            >
              <MiniPoint title="Structure incoming signals">
                Eyewitness footage and field reports are grouped into a coherent editorial flow.
              </MiniPoint>
              <MiniPoint title="Keep newsroom governance">
                Editors retain validation and publishing control while the story evolves with new material.
              </MiniPoint>
            </UseCaseSlide>
          </div>
        </section>



        <section className="hv2-section hv2-mapSnapshots" id="snapshots">
          <div className="hv2-sectionHead hv2-sectionHeadCompact hv2-mapSnapshotsTitle">
            <h2>Living snapshots are media, not just markers.</h2>
          </div>

          <p className="hv2-mapSnapshotsLead">Maps become living interfaces when each point can open a real media moment, not just a static pin.</p>

          <div
            className="hv2-mapSnapshotsVideoWrap"
            onMouseEnter={playHoverVideoWithSound}
            onMouseLeave={keepHoverVideoPlayingMuted}
          >
            <video
              className="hv2-mapSnapshotsVideo"
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              tabIndex={0}
              aria-label="Living snapshots video preview"
              onFocus={(event) => { event.currentTarget.muted = false; event.currentTarget.play().catch(() => {}); }}
              onBlur={(event) => { event.currentTarget.muted = true; event.currentTarget.play().catch(() => {}); }}
            >
              <source src={mapSectionVideo} type="video/mp4" />
            </video>
          </div>
        </section>

        <section className="hv2-section hv2-architecture" id="technology">
          <div className="hv2-sectionHead hv2-sectionHeadCompact hv2-architectureTitle">
            <h2>From Isolated Clips to Weaves</h2>
          </div>

          <div className="hv2-architectureCard">
            <img
              src={architectureImage}
              alt="Weave architecture: AI-driven curation, relay linking, dynamic rights attribution and cloud scalability"
              loading="lazy"
            />

            <div
              className="hv2-architectureVideoBlock"
              aria-label="Woven video output preview"
              onMouseEnter={playHoverVideoWithSound}
              onMouseLeave={keepHoverVideoPlayingMuted}
            >
              <video className="hv2-architectureVideo" autoPlay muted loop preload="auto" playsInline>
                <source src={relayVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </section>

        <section className="hv2-section hv2-weavers" id="weavers">
          <div className="hv2-sectionHead hv2-sectionHeadCompact hv2-weaversTitle">
            <h2>Connecting Weavers. Scaling Enterprises. Automating Value.</h2>
          </div>

          <div className="hv2-weaverImageCard">
            <img
              src={weaverNetworkImage}
              alt="Weaver Network: turn your knowledge into living value with revenue sharing, attribution, multi-screen reach and frictionless creation"
              loading="lazy"
            />
            <a
              className="hv2-weaverImageCta"
              href={REGISTER_FORM_URL}
              aria-label="Apply as a verified Weaver"
              onClick={() => trackHomeV2("apply_weaver_image_click", { href: REGISTER_FORM_URL })}
            />
          </div>
        </section>

        <p className="hv2-pageDisclaimer">
          * Brand names, logos, and trademarks referenced on this page are used strictly for illustrative and conceptual demonstration purposes. Their mention does not imply any formal affiliation, partnership, or endorsement.
        </p>

      </main>

      <PublicFooter />
    </div>
  );
}
